/**
 * Centralized Market Configuration
 * Single source of truth for all market definitions
 */

import { isTestnet } from './network';
import { TRADING_SCHEDULES } from './tradingSchedules';

// Market categories
export const MARKET_CATEGORIES = {
  CRYPTO: 'crypto',
  REAL_ESTATE: 'realestate',
  RWA: 'rwa',
  WATCHES: 'watches',
  FOREX: 'forex',
  COMMODITIES: 'commodities',
  STOCKS: 'stocks',
};

/**
 * Parse token index from environment variable
 * @param {string} marketId - Market ID (e.g., 'BITCOIN', 'ETH')
 * @returns {number|null} Token index or null if not found
 */
const getTokenIndexFromEnv = (marketId) => {
  const envVar = `REACT_APP_PERP_TOKEN_INDEXES_${marketId.toUpperCase().replace(/-/g, '_')}`;
  const envValue = process.env[envVar];
  if (envValue) {
    const match = envValue.match(/TokenIndex\((\d+)\)/);
    return match ? parseInt(match[1], 10) : null;
  }
  return null;
};

/**
 * Parse market index from environment variable
 * @param {string} marketId - Market ID
 * @returns {number|null} Market index or null if not found
 */
const getMarketIndexFromEnv = (marketId) => {
  const envVar = `REACT_APP_MARKET_INDEXES_${marketId.toUpperCase().replace(/-/g, '_')}`;
  const envValue = process.env[envVar];
  if (envValue) {
    const match = envValue.match(/MarketIndex\((\d+)\)/);
    return match ? parseInt(match[1], 10) : null;
  }
  return null;
};

/**
 * Parse token index from string or number
 * @param {number|string} tokenIndex - TokenIndex number or string
 * @returns {number} Parsed token index
 */
const parseTokenIndex = (tokenIndex) => {
  if (typeof tokenIndex === 'string') {
    const match = tokenIndex.match(/TokenIndex\((\d+)\)/);
    if (match) {
      return parseInt(match[1], 10);
    }
    return parseInt(tokenIndex, 10);
  }
  return tokenIndex;
};

/**
 * All market configurations
 * Structure:
 * - id: unique identifier (lowercase, used in routes)
 * - name: display name
 * - symbol: trading symbol
 * - tokenIndex: TokenIndex number (from contract)
 * - marketIndex: MarketIndex number (from contract) or null if no market
 * - maxLeverage: maximum leverage allowed (e.g., 100 for 100x)
 * - category: market category
 * - coinGeckoId: CoinGecko API ID for price data (null if not available)
 * - chartType: chart identifier for TradingView
 * - iconPath: path to icon image (optional)
 * - displayName: formatted display name (e.g., "BTC/USDC")
 */
export const MARKETS = [
  // Existing Crypto Markets
  {
    id: 'btc',
    name: 'Bitcoin',
    symbol: 'BTC',
    tokenIndex: getTokenIndexFromEnv('BITCOIN') ?? (isTestnet() ? 1 : 3),
    marketIndex: getMarketIndexFromEnv('BITCOIN') ?? 0,
    maxLeverage: 100,
    category: MARKET_CATEGORIES.CRYPTO,
    coinGeckoId: 'bitcoin',
    chartType: 'bitcoin',
    tradingViewSymbol: 'BITSTAMP:BTCUSD',
    iconPath: '/assets/images/Perps/bitcoin-btc-logo.png',
    displayName: 'BTC/USDC',
    description: 'Bitcoin',
    assetInfo: {
      what: 'Bitcoin (BTC) is the original decentralized digital currency, secured by proof-of-work mining. Fixed supply of 21 million coins.',
      priceDriver: 'Spot BTC price aggregated from major exchanges (Binance, Coinbase, Bitstamp). Oracle updates every ~10 seconds.',
      risks: 'High volatility. Macro sentiment, ETF flows, halving cycles, and regulatory news are the main price drivers.',
    },
  },
  {
    id: 'eth',
    name: 'Ethereum',
    symbol: 'ETH',
    tokenIndex: getTokenIndexFromEnv('ETH') ?? (isTestnet() ? 2 : 4),
    marketIndex: getMarketIndexFromEnv('ETH') ?? 1,
    maxLeverage: 100,
    category: MARKET_CATEGORIES.CRYPTO,
    coinGeckoId: 'ethereum',
    chartType: 'ethereum',
    tradingViewSymbol: 'BITSTAMP:ETHUSD',
    iconPath: '/assets/images/Perps/ethereum-eth-logo.png',
    displayName: 'ETH/USDC',
    description: 'Ethereum',
    assetInfo: {
      what: 'Ethereum (ETH) is a decentralized smart-contract platform powering DeFi, NFTs, and Web3 applications. Post-Merge, ETH is deflationary under high usage.',
      priceDriver: 'Spot ETH price from major exchanges. Influenced by DeFi activity, staking yields, and Layer-2 adoption.',
      risks: 'High volatility. Sensitive to gas fee trends, Ethereum upgrade timelines, and broader crypto market sentiment.',
    },
  },
  
  // New 100x Leverage Markets with MarketIndex
  {
    id: 'sol',
    name: 'Solana',
    symbol: 'SOL',
    tokenIndex: getTokenIndexFromEnv('SOL') ?? (isTestnet() ? 19 : 17),
    marketIndex: getMarketIndexFromEnv('SOL') ?? (isTestnet() ? 14 : 16),
    maxLeverage: 100,
    category: MARKET_CATEGORIES.CRYPTO,
    coinGeckoId: 'solana',
    chartType: 'solana',
    tradingViewSymbol: 'BINANCE:SOLUSD.P',
    iconPath: null,
    displayName: 'SOL/USDC',
    description: 'Solana',
    assetInfo: {
      what: 'Solana (SOL) is a high-throughput Layer-1 blockchain using Proof of History for fast, low-cost transactions. Powers a large DeFi and NFT ecosystem.',
      priceDriver: 'Spot SOL price from major exchanges. Network activity, DeFi TVL, and developer adoption drive sentiment.',
      risks: 'High volatility. Past network outages have caused sharp sell-offs. Ecosystem concentration risk.',
    },
  },
  {
    id: 'xrp',
    name: 'Ripple',
    symbol: 'XRP',
    tokenIndex: getTokenIndexFromEnv('XRP') ?? (isTestnet() ? 20 : 18),
    marketIndex: getMarketIndexFromEnv('XRP') ?? (isTestnet() ? 15 : 17),
    maxLeverage: 100,
    category: MARKET_CATEGORIES.CRYPTO,
    coinGeckoId: 'ripple',
    chartType: 'xrp',
    tradingViewSymbol: 'BINANCE:XRPUSD.P',
    iconPath: null,
    displayName: 'XRP/USDC',
    description: 'Ripple',
    assetInfo: {
      what: 'XRP is the native token of the XRP Ledger, designed for fast cross-border payments. Used by financial institutions via Ripple\'s payment solutions.',
      priceDriver: 'Spot XRP price. Heavily influenced by Ripple\'s legal battles (SEC case), banking partnerships, and payment volume.',
      risks: 'High volatility. Regulatory risk remains elevated. Token supply is largely held by Ripple Labs.',
    },
  },
  {
    id: 'ltc',
    name: 'Litecoin',
    symbol: 'LTC',
    tokenIndex: getTokenIndexFromEnv('LTC') ?? (isTestnet() ? 29 : 27),
    marketIndex: getMarketIndexFromEnv('LTC') ?? (isTestnet() ? 24 : 26),
    maxLeverage: 100,
    category: MARKET_CATEGORIES.CRYPTO,
    coinGeckoId: 'litecoin',
    chartType: 'litecoin',
    tradingViewSymbol: 'BINANCE:LTCUSD.P',
    iconPath: null,
    displayName: 'LTC/USDC',
    description: 'Litecoin',
    assetInfo: {
      what: 'Litecoin (LTC) is one of the oldest cryptocurrencies, forked from Bitcoin with faster block times and the Scrypt hashing algorithm.',
      priceDriver: 'Spot LTC price. Tends to follow Bitcoin\'s directional moves. Halving cycles impact miner supply.',
      risks: 'High volatility. Lower adoption vs. BTC and ETH. Development activity has slowed relative to newer chains.',
    },
  },
  {
    id: 'bnb',
    name: 'Binance Coin',
    symbol: 'BNB',
    tokenIndex: getTokenIndexFromEnv('BNB') ?? (isTestnet() ? 32 : 30),
    marketIndex: getMarketIndexFromEnv('BNB') ?? (isTestnet() ? 27 : 29),
    maxLeverage: 100,
    category: MARKET_CATEGORIES.CRYPTO,
    coinGeckoId: 'binancecoin',
    chartType: 'bnb',
    tradingViewSymbol: 'BINANCE:BNBUSD.P',
    iconPath: null,
    displayName: 'BNB/USDC',
    description: 'Binance Coin',
    assetInfo: {
      what: 'BNB is the native token of BNB Chain and Binance Exchange. Used to pay trading fees, participate in token launches, and power the BNB Smart Chain ecosystem.',
      priceDriver: 'Spot BNB price. Correlated with Binance trading volume and BNB Chain DeFi activity. Quarterly burns reduce supply.',
      risks: 'High volatility. Concentration risk: tied to Binance\'s regulatory standing and business performance.',
    },
  },
  {
    id: 'trump',
    name: 'Trump',
    symbol: 'TRUMP',
    tokenIndex: getTokenIndexFromEnv('TRUMP') ?? (isTestnet() ? 41 : 38),
    marketIndex: getMarketIndexFromEnv('TRUMP') ?? (isTestnet() ? 36 : 37),
    maxLeverage: 100,
    category: MARKET_CATEGORIES.CRYPTO,
    coinGeckoId: 'official-trump',
    chartType: 'trump',
    tradingViewSymbol: 'BINANCE:TRUMPUSDT.P',
    iconPath: null,
    displayName: 'TRUMP/USDC',
    description: 'Trump',
    assetInfo: {
      what: 'TRUMP is the Official Trump meme coin launched in January 2025 ahead of the presidential inauguration. A politically themed speculative token with no utility.',
      priceDriver: 'Purely sentiment-driven. Price spikes on political news, social media posts, and macro events.',
      risks: 'Extremely high volatility. Meme coin with no underlying utility. Majority of supply controlled by insiders.',
    },
  },
  {
    id: 'near',
    name: 'NEAR Protocol',
    symbol: 'NEAR',
    tokenIndex: getTokenIndexFromEnv('NEAR') ?? (isTestnet() ? 42 : 39),
    marketIndex: getMarketIndexFromEnv('NEAR') ?? (isTestnet() ? 37 : 38),
    maxLeverage: 100,
    category: MARKET_CATEGORIES.CRYPTO,
    coinGeckoId: 'near',
    chartType: 'near',
    tradingViewSymbol: 'BINANCE:NEARUSD.P',
    iconPath: null,
    displayName: 'NEAR/USDC',
    description: 'NEAR Protocol',
    assetInfo: {
      what: 'NEAR Protocol is a Layer-1 blockchain with sharding (Nightshade) for scalability. Focuses on developer-friendly tooling and AI integrations.',
      priceDriver: 'Spot NEAR price. Driven by ecosystem dApp growth, DeFi TVL, and AI narrative adoption.',
      risks: 'High volatility. Competes with many L1/L2 chains for developer mindshare.',
    },
  },
  {
    id: 'hype',
    name: 'Hype',
    symbol: 'HYPE',
    tokenIndex: getTokenIndexFromEnv('HYPE') ?? (isTestnet() ? 47 : 44),
    marketIndex: getMarketIndexFromEnv('HYPE') ?? (isTestnet() ? 42 : 43),
    maxLeverage: 100,
    category: MARKET_CATEGORIES.CRYPTO,
    coinGeckoId: 'hyperliquid',
    chartType: 'hype',
    tradingViewSymbol: 'BINANCE:HYPEUSDT.P',
    iconPath: null,
    displayName: 'HYPE/USDC',
    description: 'Hype',
    assetInfo: {
      what: 'HYPE is the native token of Hyperliquid, a fully on-chain perpetuals DEX. Used for governance, fee discounts, and staking on HyperEVM.',
      priceDriver: 'Spot HYPE price. Driven by Hyperliquid trading volume, protocol revenue, and DeFi expansion via HyperEVM.',
      risks: 'High volatility. Newer token with concentrated early distribution. DEX competition risk.',
    },
  },
  {
    id: 'sui',
    name: 'Sui',
    symbol: 'SUI',
    tokenIndex: getTokenIndexFromEnv('SUI') ?? (isTestnet() ? 21 : 19),
    marketIndex: getMarketIndexFromEnv('SUI') ?? (isTestnet() ? 16 : 18),
    maxLeverage: 20,
    category: MARKET_CATEGORIES.CRYPTO,
    coinGeckoId: 'sui',
    chartType: 'sui',
    tradingViewSymbol: 'BINANCE:SUIUSDC',
    iconPath: null,
    displayName: 'SUI/USDC',
    description: 'Sui',
    assetInfo: {
      what: 'Sui is a Layer-1 blockchain using the Move language, designed for high throughput and parallel transaction processing. Strong gaming and DeFi ecosystem.',
      priceDriver: 'Spot SUI price. Driven by DeFi TVL growth, gaming partnerships, and developer adoption.',
      risks: 'High volatility. Large token unlock schedules can create sell pressure. L1 competition is fierce.',
    },
  },
  {
    id: 'ena',
    name: 'Ethena',
    symbol: 'ENA',
    tokenIndex: getTokenIndexFromEnv('ENA') ?? (isTestnet() ? 22 : 20),
    marketIndex: getMarketIndexFromEnv('ENA') ?? (isTestnet() ? 17 : 19),
    maxLeverage: 20,
    category: MARKET_CATEGORIES.CRYPTO,
    coinGeckoId: 'ethena',
    chartType: 'ena',
    tradingViewSymbol: 'BYBIT:ENAUSDT.P',
    iconPath: null,
    displayName: 'ENA/USDC',
    description: 'Ethena',
    assetInfo: {
      what: 'ENA is the governance token of Ethena, issuer of USDe — a synthetic dollar backed by staked ETH and short perp hedges. One of the fastest-growing stablecoins.',
      priceDriver: 'Spot ENA price. Driven by USDe supply growth, protocol revenue, and staking yields.',
      risks: 'High volatility. USDe peg stability depends on perp funding rates — negative funding can stress reserves.',
    },
  },
  {
    id: 'arb',
    name: 'Arbitrum',
    symbol: 'ARB',
    tokenIndex: getTokenIndexFromEnv('ARB') ?? (isTestnet() ? 23 : 21),
    marketIndex: getMarketIndexFromEnv('ARB') ?? (isTestnet() ? 18 : 20),
    maxLeverage: 20,
    category: MARKET_CATEGORIES.CRYPTO,
    coinGeckoId: 'arbitrum',
    chartType: 'arb',
    tradingViewSymbol: 'COINBASE:ARBUSD',
    iconPath: null,
    displayName: 'ARB/USDC',
    description: 'Arbitrum',
    assetInfo: {
      what: 'ARB is the governance token of Arbitrum, the leading Ethereum Layer-2 using Optimistic Rollups. Home to a vast DeFi ecosystem and high TVL.',
      priceDriver: 'Spot ARB price. Driven by Arbitrum chain usage, DeFi activity, and L2 narrative strength.',
      risks: 'High volatility. Large token unlocks from team/investors are ongoing. L2 competition from Optimism, Base, and others.',
    },
  },
  {
    id: 'trx',
    name: 'Tron',
    symbol: 'TRX',
    tokenIndex: getTokenIndexFromEnv('TRX') ?? (isTestnet() ? 24 : 22),
    marketIndex: getMarketIndexFromEnv('TRX') ?? (isTestnet() ? 19 : 21),
    maxLeverage: 5,
    category: MARKET_CATEGORIES.CRYPTO,
    coinGeckoId: 'tron',
    chartType: 'trx',
    tradingViewSymbol: 'CRYPTO:TRXUSD',
    iconPath: null,
    displayName: 'TRX/USDC',
    description: 'Tron',
    assetInfo: {
      what: 'TRX is the native token of the Tron network, a high-throughput blockchain popular for USDT transfers in emerging markets.',
      priceDriver: 'Spot TRX price. Driven by USDT on-chain volume on Tron and staking activity.',
      risks: 'Moderate–high volatility. Centralization concerns. Founder Justin Sun\'s regulatory history creates headline risk.',
    },
  },
  {
    id: 'apt',
    name: 'Aptos',
    symbol: 'APT',
    tokenIndex: getTokenIndexFromEnv('APT') ?? (isTestnet() ? 25 : 23),
    marketIndex: getMarketIndexFromEnv('APT') ?? (isTestnet() ? 20 : 22),
    maxLeverage: 5,
    category: MARKET_CATEGORIES.CRYPTO,
    coinGeckoId: 'aptos',
    chartType: 'apt',
    tradingViewSymbol: 'COINBASE:APTUSD',
    iconPath: null,
    displayName: 'APT/USDC',
    description: 'Aptos',
    assetInfo: {
      what: 'Aptos is a Layer-1 blockchain using the Move language, built by ex-Meta engineers. Designed for safety and high throughput.',
      priceDriver: 'Spot APT price. Driven by DeFi/gaming adoption and developer ecosystem growth.',
      risks: 'High volatility. Large token unlock schedule. Competes with Sui (same Move language heritage).',
    },
  },
  {
    id: 'pol',
    name: 'Polygon',
    symbol: 'POL',
    tokenIndex: getTokenIndexFromEnv('POL') ?? (isTestnet() ? 26 : 24),
    marketIndex: getMarketIndexFromEnv('POL') ?? (isTestnet() ? 21 : 23),
    maxLeverage: 5,
    category: MARKET_CATEGORIES.CRYPTO,
    coinGeckoId: 'polygon-ecosystem-token',
    chartType: 'pol',
    tradingViewSymbol: 'COINBASE:POLUSD',
    iconPath: null,
    displayName: 'POL/USDC',
    description: 'Polygon',
    assetInfo: {
      what: 'POL (formerly MATIC) is the native token of Polygon, an Ethereum scaling suite. Powers the AggLayer, zkEVM, and multiple enterprise blockchain solutions.',
      priceDriver: 'Spot POL price. Driven by Polygon ecosystem usage and the zkEVM narrative.',
      risks: 'High volatility. Migration from MATIC to POL adds complexity. Faces competition from other ZK L2s.',
    },
  },
  {
    id: 'ton',
    name: 'Toncoin',
    symbol: 'TON',
    tokenIndex: getTokenIndexFromEnv('TON') ?? (isTestnet() ? 27 : 25),
    marketIndex: getMarketIndexFromEnv('TON') ?? (isTestnet() ? 22 : 24),
    maxLeverage: 5,
    category: MARKET_CATEGORIES.CRYPTO,
    coinGeckoId: 'the-open-network',
    chartType: 'ton',
    tradingViewSymbol: 'COINBASE:TONUSD',
    iconPath: null,
    displayName: 'TON/USDC',
    description: 'Toncoin',
    assetInfo: {
      what: 'TON (The Open Network) is the blockchain developed by the Telegram team. Deep integration with Telegram\'s 900M+ user base drives adoption for payments and mini-apps.',
      priceDriver: 'Spot TON price. Strongly tied to Telegram ecosystem growth, mini-app usage, and developer activity.',
      risks: 'High volatility. Regulatory risk given Telegram\'s global reach. Founder Pavel Durov\'s legal situation creates headline risk.',
    },
  },
  {
    id: 'ada',
    name: 'Cardano',
    symbol: 'ADA',
    tokenIndex: getTokenIndexFromEnv('ADA') ?? (isTestnet() ? 28 : 26),
    marketIndex: getMarketIndexFromEnv('ADA') ?? (isTestnet() ? 23 : 25),
    maxLeverage: 5,
    category: MARKET_CATEGORIES.CRYPTO,
    coinGeckoId: 'cardano',
    chartType: 'ada',
    tradingViewSymbol: 'COINBASE:ADAUSD',
    iconPath: null,
    displayName: 'ADA/USDC',
    description: 'Cardano',
    assetInfo: {
      what: 'Cardano (ADA) is a research-driven Layer-1 blockchain using Proof of Stake. Known for its peer-reviewed academic approach to protocol development.',
      priceDriver: 'Spot ADA price. Driven by staking yields, smart contract usage, and community sentiment.',
      risks: 'High volatility. Development has been slower than competitors. DeFi ecosystem lags behind Ethereum and Solana.',
    },
  },
  {
    id: 'doge',
    name: 'Dogecoin',
    symbol: 'DOGE',
    tokenIndex: getTokenIndexFromEnv('DOGE') ?? (isTestnet() ? 30 : 28),
    marketIndex: getMarketIndexFromEnv('DOGE') ?? (isTestnet() ? 25 : 27),
    maxLeverage: 20,
    category: MARKET_CATEGORIES.CRYPTO,
    coinGeckoId: 'dogecoin',
    chartType: 'doge',
    tradingViewSymbol: 'COINBASE:DOGEUSD',
    iconPath: null,
    displayName: 'DOGE/USDC',
    description: 'Dogecoin',
    assetInfo: {
      what: 'Dogecoin (DOGE) is the original meme coin, started as a joke in 2013. Has grown into a top-10 asset driven by community culture and celebrity endorsements.',
      priceDriver: 'Spot DOGE price. Elon Musk tweets and social media virality are the primary short-term drivers.',
      risks: 'Extremely high volatility. No hard cap on supply (inflation ~5B DOGE/year). Purely sentiment-driven.',
    },
  },
  {
    id: 'aster',
    name: 'Aster',
    symbol: 'ASTER',
    tokenIndex: getTokenIndexFromEnv('ASTER') ?? (isTestnet() ? 31 : 29),
    marketIndex: getMarketIndexFromEnv('ASTER') ?? (isTestnet() ? 26 : 28),
    maxLeverage: 5,
    category: MARKET_CATEGORIES.CRYPTO,
    coinGeckoId: 'aster-2',
    chartType: 'aster',
    tradingViewSymbol: 'CRYPTO:ASTER2USD',
    iconPath: null,
    displayName: 'ASTER/USDC',
    description: 'Aster',
    assetInfo: {
      what: 'Aster is a DeFi/restaking protocol token. Provides staking and yield optimization across multiple networks.',
      priceDriver: 'Spot ASTER price. Driven by restaking narrative, TVL growth, and DeFi yield environment.',
      risks: 'High volatility. Small-cap token with limited liquidity. Highly sensitive to broader DeFi sentiment.',
    },
  },
  {
    id: 'pump',
    name: 'Pump',
    symbol: 'PUMP',
    tokenIndex: getTokenIndexFromEnv('PUMP') ?? (isTestnet() ? 33 : 31),
    marketIndex: getMarketIndexFromEnv('PUMP') ?? (isTestnet() ? 28 : 30),
    maxLeverage: 5,
    category: MARKET_CATEGORIES.CRYPTO,
    coinGeckoId: 'pump-fun',
    chartType: 'pump',
    tradingViewSymbol: 'COINBASE:PUMPUSD',
    iconPath: null,
    displayName: 'PUMP/USDC',
    description: 'Pump',
    assetInfo: {
      what: 'PUMP is the native token of Pump.fun, the dominant Solana meme coin launchpad. The platform has facilitated millions of token launches.',
      priceDriver: 'Spot PUMP price. Driven by Solana meme coin season activity and launchpad fee revenue.',
      risks: 'Extremely high volatility. Meme coin narrative. Revenue closely tied to speculative market cycles.',
    },
  },
  {
    id: 'avax',
    name: 'Avalanche',
    symbol: 'AVAX',
    tokenIndex: getTokenIndexFromEnv('AVAX') ?? (isTestnet() ? 34 : 32),
    marketIndex: getMarketIndexFromEnv('AVAX') ?? (isTestnet() ? 29 : 31),
    maxLeverage: 20,
    category: MARKET_CATEGORIES.CRYPTO,
    coinGeckoId: 'avalanche-2',
    chartType: 'avax',
    tradingViewSymbol: 'COINBASE:AVAXUSD',
    iconPath: null,
    displayName: 'AVAX/USDC',
    description: 'Avalanche',
    assetInfo: {
      what: 'Avalanche (AVAX) is a Layer-1 blockchain with sub-second finality. Known for its subnet architecture, enterprise blockchain adoption, and gaming/DeFi ecosystem.',
      priceDriver: 'Spot AVAX price. Driven by subnet launches, DeFi TVL, and institutional partnerships.',
      risks: 'High volatility. Competition from Solana and Ethereum L2s for DeFi liquidity.',
    },
  },
  {
    id: 'aave',
    name: 'Aave',
    symbol: 'AAVE',
    tokenIndex: getTokenIndexFromEnv('AAVE') ?? (isTestnet() ? 35 : 33),
    marketIndex: getMarketIndexFromEnv('AAVE') ?? (isTestnet() ? 30 : 32),
    maxLeverage: 20,
    category: MARKET_CATEGORIES.CRYPTO,
    coinGeckoId: 'aave',
    chartType: 'aave',
    tradingViewSymbol: 'COINBASE:AAVEUSD',
    iconPath: null,
    displayName: 'AAVE/USDC',
    description: 'Aave',
    assetInfo: {
      what: 'AAVE is the governance token of Aave Protocol, the largest decentralized lending/borrowing protocol. Operates across 12+ blockchain networks.',
      priceDriver: 'Spot AAVE price. Driven by protocol TVL, borrowing demand, and DeFi usage cycles.',
      risks: 'High volatility. Smart contract risk. Liquidation cascades in volatile markets can impact protocol health.',
    },
  },
  {
    id: 'pengu',
    name: 'Pengu',
    symbol: 'PENGU',
    tokenIndex: getTokenIndexFromEnv('PENGU') ?? (isTestnet() ? 36 : 34),
    marketIndex: getMarketIndexFromEnv('PENGU') ?? (isTestnet() ? 31 : 33),
    maxLeverage: 5,
    category: MARKET_CATEGORIES.CRYPTO,
    coinGeckoId: 'pudgy-penguins',
    chartType: 'pengu',
    tradingViewSymbol: 'COINBASE:PENGUUSD',
    iconPath: null,
    displayName: 'PENGU/USDC',
    description: 'Pengu',
    assetInfo: {
      what: 'PENGU is the token of the Pudgy Penguins NFT brand, one of the most recognized NFT collections. Has expanded into physical toys and a broader consumer brand.',
      priceDriver: 'Spot PENGU price. Tied to Pudgy Penguins NFT floor prices and brand expansion news.',
      risks: 'Extremely high volatility. NFT market is highly speculative. Brand value can deteriorate quickly.',
    },
  },
  {
    id: 'link',
    name: 'Chainlink',
    symbol: 'LINK',
    tokenIndex: getTokenIndexFromEnv('LINK') ?? (isTestnet() ? 37 : 35),
    marketIndex: getMarketIndexFromEnv('LINK') ?? (isTestnet() ? 32 : 34),
    maxLeverage: 5,
    category: MARKET_CATEGORIES.CRYPTO,
    coinGeckoId: 'chainlink',
    chartType: 'link',
    tradingViewSymbol: 'COINBASE:LINKUSD',
    iconPath: null,
    displayName: 'LINK/USDC',
    description: 'Chainlink',
    assetInfo: {
      what: 'LINK is the utility token of Chainlink, the dominant decentralized oracle network. Powers price feeds and verifiable randomness used by thousands of DeFi protocols.',
      priceDriver: 'Spot LINK price. Driven by oracle network usage, new integrations, and CCIP (cross-chain) adoption.',
      risks: 'High volatility. Competition from alternative oracle solutions. Staking adoption has been gradual.',
    },
  },
  {
    id: 'bonk',
    name: 'Bonk',
    symbol: 'BONK',
    tokenIndex: getTokenIndexFromEnv('BONK') ?? (isTestnet() ? 38 : 36),
    marketIndex: getMarketIndexFromEnv('BONK') ?? (isTestnet() ? 33 : 35),
    maxLeverage: 5,
    category: MARKET_CATEGORIES.CRYPTO,
    coinGeckoId: 'bonk',
    chartType: 'bonk',
    tradingViewSymbol: 'COINBASE:BONKUSD',
    iconPath: null,
    displayName: 'BONK/USDC',
    description: 'Bonk',
    assetInfo: {
      what: 'BONK is the first major Solana meme coin, airdropped to the Solana community in late 2022 to revive network activity post-FTX collapse.',
      priceDriver: 'Spot BONK price. Driven by Solana ecosystem activity and meme coin sentiment cycles.',
      risks: 'Extremely high volatility. Meme coin with no intrinsic utility. Price can drop 80%+ in bear cycles.',
    },
  },
  {
    id: 'shib',
    name: 'Shiba Inu',
    symbol: 'SHIB',
    tokenIndex: getTokenIndexFromEnv('SHIB') ?? (isTestnet() ? 39 : 37),
    marketIndex: getMarketIndexFromEnv('SHIB') ?? (isTestnet() ? 34 : 36),
    maxLeverage: 5,
    category: MARKET_CATEGORIES.CRYPTO,
    coinGeckoId: 'shiba-inu',
    chartType: 'shib',
    tradingViewSymbol: 'COINBASE:SHIBUSD',
    iconPath: null,
    displayName: 'SHIB/USDC',
    description: 'Shiba Inu',
    assetInfo: {
      what: 'SHIB is a dog-themed meme coin on Ethereum, often called the "Dogecoin Killer." Has expanded into a broader ecosystem including Shibarium L2 and ShibaSwap DEX.',
      priceDriver: 'Spot SHIB price. Driven by meme coin cycles, token burn campaigns, and retail social media sentiment.',
      risks: 'Extremely high volatility. Quadrillions in total supply. Purely speculative — burns are marginal relative to supply.',
    },
  },
  {
    id: 'kaito',
    name: 'Kaito',
    symbol: 'KAITO',
    tokenIndex: getTokenIndexFromEnv('KAITO') ?? (isTestnet() ? 43 : 40),
    marketIndex: getMarketIndexFromEnv('KAITO') ?? (isTestnet() ? 38 : 39),
    maxLeverage: 5,
    category: MARKET_CATEGORIES.CRYPTO,
    coinGeckoId: 'kaito',
    chartType: 'kaito',
    tradingViewSymbol: 'COINBASE:KAITOUSD',
    iconPath: null,
    displayName: 'KAITO/USDC',
    description: 'Kaito',
    assetInfo: {
      what: 'KAITO is the token of Kaito AI, a crypto intelligence platform that aggregates and ranks information across social media and research sources.',
      priceDriver: 'Spot KAITO price. Driven by AI narrative, platform user growth, and Yapper airdrop campaigns.',
      risks: 'High volatility. Early-stage token. Value tied to AI information market adoption.',
    },
  },
  {
    id: 'mnt',
    name: 'Mantle',
    symbol: 'MNT',
    tokenIndex: getTokenIndexFromEnv('MNT') ?? (isTestnet() ? 44 : 41),
    marketIndex: getMarketIndexFromEnv('MNT') ?? (isTestnet() ? 39 : 40),
    maxLeverage: 5,
    category: MARKET_CATEGORIES.CRYPTO,
    coinGeckoId: 'mantle',
    chartType: 'mnt',
    tradingViewSymbol: 'CRYPTO:MNTUSD',
    iconPath: null,
    displayName: 'MNT/USDC',
    description: 'Mantle',
    assetInfo: {
      what: 'MNT is the native token of Mantle Network, an Ethereum Layer-2 backed by the BitDAO treasury. Powers an ecosystem of DeFi apps and the mETH liquid staking protocol.',
      priceDriver: 'Spot MNT price. Driven by Mantle L2 TVL, mETH staking growth, and treasury deployments.',
      risks: 'High volatility. Large treasury creates governance overhang. L2 space is intensely competitive.',
    },
  },
  {
    id: 'ip',
    name: 'Story',
    symbol: 'IP',
    tokenIndex: getTokenIndexFromEnv('IP') ?? (isTestnet() ? 45 : 42),
    marketIndex: getMarketIndexFromEnv('IP') ?? (isTestnet() ? 40 : 41),
    maxLeverage: 5,
    category: MARKET_CATEGORIES.CRYPTO,
    coinGeckoId: 'story-2',
    chartType: 'ip',
    tradingViewSymbol: 'COINBASE:IPUSDC',
    iconPath: null,
    displayName: 'IP/USDC',
    description: 'IP',
    assetInfo: {
      what: 'IP is the native token of Story Protocol, a blockchain for programmable intellectual property. Enables creators to register, license, and monetize IP on-chain.',
      priceDriver: 'Spot IP price. Driven by creator adoption, IP licensing volumes, and AI content narrative.',
      risks: 'High volatility. Early-stage protocol in an unproven market. Regulatory uncertainty around on-chain IP.',
    },
  },
  {
    id: 'virtual',
    name: 'Virtual',
    symbol: 'VIRTUAL',
    tokenIndex: getTokenIndexFromEnv('VIRTUAL') ?? (isTestnet() ? 46 : 43),
    marketIndex: getMarketIndexFromEnv('VIRTUAL') ?? (isTestnet() ? 41 : 42),
    maxLeverage: 5,
    category: MARKET_CATEGORIES.CRYPTO,
    coinGeckoId: 'virtual-protocol',
    chartType: 'virtual',
    tradingViewSymbol: 'CRYPTO:VIRTUALUSD',
    iconPath: null,
    displayName: 'VIRTUAL/USDC',
    description: 'Virtual',
    assetInfo: {
      what: 'VIRTUAL is the token of Virtuals Protocol, a platform for creating and co-owning AI agents. Enables tokenized AI agents that can generate revenue and be traded.',
      priceDriver: 'Spot VIRTUAL price. Driven by AI agent narrative, platform launches, and agent revenue metrics.',
      risks: 'Extremely high volatility. Speculative AI agent category. Token price highly correlated with hype cycles.',
    },
  },
  {
    id: 'zec',
    name: 'Zcash',
    symbol: 'ZEC',
    tokenIndex: getTokenIndexFromEnv('ZEC') ?? (isTestnet() ? 48 : 45),
    marketIndex: getMarketIndexFromEnv('ZEC') ?? (isTestnet() ? 43 : 44),
    maxLeverage: 5,
    category: MARKET_CATEGORIES.CRYPTO,
    coinGeckoId: 'zcash',
    chartType: 'zec',
    tradingViewSymbol: 'COINBASE:ZECUSD',
    iconPath: null,
    displayName: 'ZEC/USDC',
    description: 'Zcash',
    assetInfo: {
      what: 'Zcash (ZEC) is a privacy-focused cryptocurrency using zero-knowledge proofs (zk-SNARKs) to enable shielded transactions. Founded by cryptography researchers.',
      priceDriver: 'Spot ZEC price. Driven by privacy narrative, regulatory climate, and halving cycles.',
      risks: 'High volatility. Privacy coins face exchange delisting pressure. Limited DeFi ecosystem.',
    },
  },
  {
    id: 'nibi',
    name: 'Nibiru',
    symbol: 'NIBI',
    tokenIndex: getTokenIndexFromEnv('NIBI') ?? 49,
    marketIndex: getMarketIndexFromEnv('NIBI') ?? (isTestnet() ? 44 : 48),
    maxLeverage: 5,
    category: MARKET_CATEGORIES.CRYPTO,
    coinGeckoId: 'nibiru',
    chartType: 'nibi',
    tradingViewSymbol: 'CRYPTO:NIBIUSD',
    iconPath: null,
    displayName: 'NIBI/USDC',
    description: 'Nibiru',
    assetInfo: {
      what: 'NIBI is the native token of Nibiru Chain, a Cosmos-based smart contract platform. Designed for DeFi with a focus on perpetuals, stablecoins, and EVM compatibility.',
      priceDriver: 'Spot NIBI price. Driven by Nibiru ecosystem adoption, IBC volume, and DeFi TVL.',
      risks: 'High volatility. Early-stage chain competing in a crowded Cosmos ecosystem.',
    },
  },
  {
    id: 'poke',
    name: 'Pokémon',
    symbol: 'POKE',
    tokenIndex: getTokenIndexFromEnv('POKE') ?? (isTestnet() ? 53 : 50),
    marketIndex: getMarketIndexFromEnv('POKE') ?? (isTestnet() ? 45 : 49),
    // The leverage bar runs to this value. It is no longer a fallback: the
    // config is the floor and the keeper's on-chain max can only raise it, so
    // the Pokédex dial reaches 100x regardless of what the keeper reports.
    // The contract still enforces its own max at open time.
    maxLeverage: 100,
    category: MARKET_CATEGORIES.RWA,
    coinGeckoId: 'pokemon',
    chartType: 'poke',
    tradingViewSymbol: 'CRYPTO:POKEUSD',
    iconPath: '/assets/images/Perps/pokeball.svg',
    displayName: 'POKE/USDC',
    description: 'Pokémon',
    assetInfo: {
      what: 'The POKE Index tracks the floor price of PSA 10 and PSA 9 graded Pokémon cards — the highest professional grading tiers. Only sealed, authenticated cards from PSA are included. PSA 10 = "Gem Mint" (virtually perfect); PSA 9 = "Mint" (minor imperfections).',
      priceDriver: 'Weighted average of PSA 10/9 sales across key cards (e.g., Base Set Charizard, Pikachu Illustrator). Prices sourced from authenticated secondary market sales and auction houses. Price updates once daily.',
      risks: 'Prices update once daily, giving you more time to analyze moves. The collectibles market has shown strong long-term appreciation trends, driven by generational nostalgia and a growing global collector base.',
    },
  },

  // Watch Markets - Testnet: GroupIndex(6), TokenIndex 79-86, MarketIndex 74-81
  //                  Mainnet: GroupIndex(3), TokenIndex 51-58, MarketIndex 50-57
  {
    id: 'watch-index',
    name: 'Watch Index',
    symbol: 'WATCH',
    tokenIndex: getTokenIndexFromEnv('WATCH_INDEX') ?? (isTestnet() ? 79 : 51),
    marketIndex: getMarketIndexFromEnv('WATCH_INDEX') ?? (isTestnet() ? 74 : 50),
    maxLeverage: 100,
    category: MARKET_CATEGORIES.WATCHES,
    coinGeckoId: null,
    chartType: 'watch-index',
    tradingViewSymbol: null,
    iconPath: '/assets/images/Perps/watch/watch-index.png',
    displayName: 'WATCH/USDC',
    description: 'Watch Market Index',
    watchBrandKey: 'ALL_WATCHES',
    assetInfo: {
      what: 'A basket index of pre-owned luxury watch prices across all major brands (Rolex, Patek Philippe, AP, Omega, Cartier, Breitling, Tudor). Tracks the overall luxury watch market.',
      priceDriver: 'Weighted average of verified secondary market sale prices sourced from authenticated watch dealers and auction platforms. Price updates once daily.',
      risks: 'Prices update once daily. Luxury watches have historically been a strong store of value, with top-tier brands consistently appreciating over time. A well-established alternative asset class with growing mainstream interest.',
    },
  },
  {
    id: 'rolex',
    name: 'Rolex',
    symbol: 'ROLEX',
    tokenIndex: getTokenIndexFromEnv('ROLEX') ?? (isTestnet() ? 80 : 52),
    marketIndex: getMarketIndexFromEnv('ROLEX') ?? (isTestnet() ? 75 : 51),
    maxLeverage: 100,
    category: MARKET_CATEGORIES.WATCHES,
    coinGeckoId: null,
    chartType: 'watch-rolex',
    tradingViewSymbol: null,
    iconPath: '/assets/images/Perps/watch/rolex.png',
    displayName: 'ROLEX/USDC',
    description: 'Rolex Watch Index',
    watchBrandKey: 'ROLEX',
    assetInfo: {
      what: 'Tracks the average pre-owned resale price of Rolex watches across popular references (Submariner, Daytona, GMT-Master II, Datejust). Rolex is the world\'s most traded luxury watch brand.',
      priceDriver: 'Weighted average of secondary market sales from authenticated dealers. Key references: Submariner ~$10-14K, Daytona ~$17-35K (ref-dependent). Price updates once daily.',
      risks: 'Prices update once daily. Rolex is the world\'s most liquid luxury watch brand with strong resale demand. Historically one of the best-performing alternative assets with consistent buyer interest across economic cycles.',
    },
  },
  {
    id: 'patek-philippe',
    name: 'Patek Philippe',
    symbol: 'PATEK',
    tokenIndex: getTokenIndexFromEnv('PATEK_PHILIPPE') ?? (isTestnet() ? 81 : 53),
    marketIndex: getMarketIndexFromEnv('PATEK_PHILIPPE') ?? (isTestnet() ? 76 : 52),
    maxLeverage: 100,
    category: MARKET_CATEGORIES.WATCHES,
    coinGeckoId: null,
    chartType: 'watch-patek',
    tradingViewSymbol: null,
    iconPath: '/assets/images/Perps/watch/patek.png',
    displayName: 'PATEK/USDC',
    description: 'Patek Philippe Watch Index',
    watchBrandKey: 'PATEK_PHILIPPE',
    assetInfo: {
      what: 'Tracks pre-owned Patek Philippe watches — considered the pinnacle of Swiss watchmaking. Key references include the Nautilus (Ref. 5711), Calatrava, and Aquanaut.',
      priceDriver: 'Secondary market sales from vetted auction houses and dealers. Nautilus 5711 drives significant index weight given its cult status. Price updates once daily.',
      risks: 'Prices update once daily. Patek Philippe is widely regarded as the most prestigious watchmaker in the world. Iconic references like the Nautilus have shown remarkable long-term appreciation, making it a prized collector asset.',
    },
  },
  {
    id: 'audemars-piguet',
    name: 'Audemars Piguet',
    symbol: 'AP',
    tokenIndex: getTokenIndexFromEnv('AUDEMARS_PIGUET') ?? (isTestnet() ? 82 : 54),
    marketIndex: getMarketIndexFromEnv('AUDEMARS_PIGUET') ?? (isTestnet() ? 77 : 53),
    maxLeverage: 100,
    category: MARKET_CATEGORIES.WATCHES,
    coinGeckoId: null,
    chartType: 'watch-ap',
    tradingViewSymbol: null,
    iconPath: '/assets/images/Perps/watch/ap.png',
    displayName: 'AP/USDC',
    description: 'Audemars Piguet Watch Index',
    watchBrandKey: 'AUDEMARS_PIGUET',
    assetInfo: {
      what: 'Tracks pre-owned Audemars Piguet watches. Dominated by the Royal Oak (Ref. 15202, 15500) — one of the most iconic luxury sports watches ever made.',
      priceDriver: 'Secondary market transaction data from authenticated dealers. Royal Oak references drive the majority of index value. Price updates once daily.',
      risks: 'Prices update once daily. The Royal Oak is one of the most iconic watches ever created, with strong collector demand and limited production. AP\'s exclusivity-driven strategy supports long-term value retention.',
    },
  },
  {
    id: 'omega',
    name: 'Omega',
    symbol: 'OMEGA',
    tokenIndex: getTokenIndexFromEnv('OMEGA') ?? (isTestnet() ? 83 : 55),
    marketIndex: getMarketIndexFromEnv('OMEGA') ?? (isTestnet() ? 78 : 54),
    maxLeverage: 100,
    category: MARKET_CATEGORIES.WATCHES,
    coinGeckoId: null,
    chartType: 'watch-omega',
    tradingViewSymbol: null,
    iconPath: '/assets/images/Perps/watch/omega.png',
    displayName: 'OMEGA/USDC',
    description: 'Omega Watch Index',
    watchBrandKey: 'OMEGA',
    assetInfo: {
      what: 'Tracks pre-owned Omega watches. Key references include the Speedmaster "Moonwatch", Seamaster 300M, and Constellation. The Speedmaster is the most historically significant Omega.',
      priceDriver: 'Secondary market transactions across Speedmaster, Seamaster, and Constellation lines. Speedmaster anniversary editions can move the index. Price updates once daily.',
      risks: 'Prices update once daily. Omega offers a more accessible entry point into luxury watches with strong brand heritage. The Speedmaster\'s historic significance (NASA\'s moon watch) provides enduring collector appeal.',
    },
  },
  {
    id: 'cartier',
    name: 'Cartier',
    symbol: 'CARTIER',
    tokenIndex: getTokenIndexFromEnv('CARTIER') ?? (isTestnet() ? 84 : 56),
    marketIndex: getMarketIndexFromEnv('CARTIER') ?? (isTestnet() ? 79 : 55),
    maxLeverage: 100,
    category: MARKET_CATEGORIES.WATCHES,
    coinGeckoId: null,
    chartType: 'watch-cartier',
    tradingViewSymbol: null,
    iconPath: '/assets/images/Perps/watch/cartier.png',
    displayName: 'CARTIER/USDC',
    description: 'Cartier Watch Index',
    watchBrandKey: 'CARTIER',
    assetInfo: {
      what: 'Tracks pre-owned Cartier watches. Iconic references include the Santos, Tank, Ballon Bleu, and Panthère. Cartier straddles the line between jewelry and watchmaking.',
      priceDriver: 'Secondary market sales across Cartier\'s major collections. Santos and Tank drive volume; Panthère de Cartier has shown strong appreciation. Price updates once daily.',
      risks: 'Prices update once daily. Cartier uniquely bridges the jewelry and watch worlds, giving it broad appeal beyond traditional collectors. The Panthère and Santos lines have shown strong recent appreciation trends.',
    },
  },
  {
    id: 'breitling',
    name: 'Breitling',
    symbol: 'BREITLING',
    tokenIndex: getTokenIndexFromEnv('BREITLING') ?? (isTestnet() ? 85 : 57),
    marketIndex: getMarketIndexFromEnv('BREITLING') ?? (isTestnet() ? 80 : 56),
    maxLeverage: 100,
    category: MARKET_CATEGORIES.WATCHES,
    coinGeckoId: null,
    chartType: 'watch-breitling',
    tradingViewSymbol: null,
    iconPath: '/assets/images/Perps/watch/breitling.png',
    displayName: 'BREITLING/USDC',
    description: 'Breitling Watch Index',
    watchBrandKey: 'BREITLING',
    assetInfo: {
      what: 'Tracks pre-owned Breitling watches. Known for aviation and diving instruments. Key references: Navitimer, Chronomat, Superocean, and Avenger collections.',
      priceDriver: 'Secondary market sales from dealers. Navitimer\'s historical significance drives collector demand. Price updates once daily.',
      risks: 'Prices update once daily. Breitling offers solid value in the luxury segment with a loyal aviation-enthusiast collector base. Less prone to speculative swings, offering a more measured exposure to the luxury watch market.',
    },
  },
  {
    id: 'tudor',
    name: 'Tudor',
    symbol: 'TUDOR',
    tokenIndex: getTokenIndexFromEnv('TUDOR') ?? (isTestnet() ? 86 : 58),
    marketIndex: getMarketIndexFromEnv('TUDOR') ?? (isTestnet() ? 81 : 57),
    maxLeverage: 100,
    category: MARKET_CATEGORIES.WATCHES,
    coinGeckoId: null,
    chartType: 'watch-tudor',
    tradingViewSymbol: null,
    iconPath: '/assets/images/Perps/watch/tudor.png',
    displayName: 'TUDOR/USDC',
    description: 'Tudor Watch Index',
    watchBrandKey: 'TUDOR',
    assetInfo: {
      what: 'Tracks pre-owned Tudor watches — Rolex\'s sister brand offering Swiss quality at accessible luxury prices. Key references: Black Bay (BB58, BB36), Pelagos, and Heritage Ranger.',
      priceDriver: 'Secondary market transactions. Black Bay 58 is the most liquid and heavily weighted reference. Price updates once daily.',
      risks: 'Prices update once daily. Tudor offers Rolex-level quality at more accessible prices, with contained downside risk. A great entry point into the luxury watch market with growing brand recognition and collector interest.',
    },
  },

  // ── Stock Markets ──────────────────────────────────────────────────
  // Group: equities_commodities (GroupIndex 4)
  // Trading hours: Mon–Fri 9:30 AM – 4:00 PM ET
  // On-chain: TokenIndex 59–76, MarketIndex 1000–1017

  // ETFs
  {
    id: 'qqq',
    name: 'QQQ',
    symbol: 'QQQ',
    tokenIndex: getTokenIndexFromEnv('QQQ') ?? 59,
    marketIndex: getMarketIndexFromEnv('QQQ') ?? 1000,
    maxLeverage: 100,
    category: MARKET_CATEGORIES.STOCKS,
    tradingSchedule: TRADING_SCHEDULES.US_STOCKS,
    coinGeckoId: null,
    chartType: 'qqq',
    tradingViewSymbol: 'NASDAQ:QQQ',
    iconPath: '/assets/images/Perps/stocks/qqq.svg',
    displayName: 'QQQ/USDC',
    description: 'Invesco QQQ Trust',
    assetInfo: {
      what: 'QQQ tracks the Nasdaq-100 Index — the 100 largest non-financial companies listed on NASDAQ. Heavy tech weighting (Apple, Microsoft, NVIDIA, Meta, Amazon).',
      priceDriver: 'Real-time ETF price from major US exchanges. Driven by mega-cap tech earnings, AI capex cycles, and interest rate expectations.',
      risks: 'Market hours only (Mon–Fri 9:30 AM – 4:00 PM ET). Tech-sector concentration means outsized moves on FAANG earnings days.',
    },
  },
  {
    id: 'spy',
    name: 'SPY',
    symbol: 'SPY',
    tokenIndex: getTokenIndexFromEnv('SPY') ?? 60,
    marketIndex: getMarketIndexFromEnv('SPY') ?? 1001,
    maxLeverage: 100,
    category: MARKET_CATEGORIES.STOCKS,
    tradingSchedule: TRADING_SCHEDULES.US_STOCKS,
    coinGeckoId: null,
    chartType: 'spy',
    tradingViewSymbol: 'AMEX:SPY',
    iconPath: '/assets/images/Perps/stocks/spy.svg',
    displayName: 'SPY/USDC',
    description: 'SPDR S&P 500 ETF',
    assetInfo: {
      what: 'SPY tracks the S&P 500 Index — 500 leading US public companies. The most traded ETF in the world and the benchmark for US equities.',
      priceDriver: 'Real-time ETF price. Driven by macro data (CPI, jobs, Fed policy), corporate earnings season, and global risk appetite.',
      risks: 'Market hours only (Mon–Fri 9:30 AM – 4:00 PM ET). Broad market exposure means lower single-stock risk but full macro sensitivity.',
    },
  },

  // Individual Stocks
  {
    id: 'nvidia',
    name: 'NVIDIA',
    symbol: 'NVDA',
    tokenIndex: getTokenIndexFromEnv('NVIDIA') ?? 61,
    marketIndex: getMarketIndexFromEnv('NVIDIA') ?? 1002,
    maxLeverage: 100,
    category: MARKET_CATEGORIES.STOCKS,
    tradingSchedule: TRADING_SCHEDULES.US_STOCKS,
    coinGeckoId: null,
    chartType: 'nvidia',
    tradingViewSymbol: 'NASDAQ:NVDA',
    iconPath: '/assets/images/Perps/stocks/nvda.svg',
    displayName: 'NVDA/USDC',
    description: 'NVIDIA Corporation',
    assetInfo: {
      what: 'NVIDIA designs GPUs and AI accelerators. Dominant position in data-center AI chips (H100/B100). The most valuable semiconductor company globally.',
      priceDriver: 'Stock price from NASDAQ. Driven by data-center revenue guidance, AI capex from hyperscalers, and export control regulations.',
      risks: 'Market hours only. Extreme volatility around earnings. Valuation relies on sustained AI infrastructure spending growth.',
    },
  },
  {
    id: 'amd',
    name: 'AMD',
    symbol: 'AMD',
    tokenIndex: getTokenIndexFromEnv('AMD') ?? 62,
    marketIndex: getMarketIndexFromEnv('AMD') ?? 1003,
    maxLeverage: 100,
    category: MARKET_CATEGORIES.STOCKS,
    tradingSchedule: TRADING_SCHEDULES.US_STOCKS,
    coinGeckoId: null,
    chartType: 'amd',
    tradingViewSymbol: 'NASDAQ:AMD',
    iconPath: '/assets/images/Perps/stocks/amd.svg',
    displayName: 'AMD/USDC',
    description: 'Advanced Micro Devices',
    assetInfo: {
      what: 'AMD designs CPUs and GPUs for PCs, gaming, and data centers. Key NVIDIA competitor in the AI accelerator market with its MI300 series.',
      priceDriver: 'Stock price from NASDAQ. Driven by data-center GPU market share gains, PC/gaming cycles, and AI chip demand.',
      risks: 'Market hours only. Competes directly with NVIDIA and Intel. Market share shifts can cause large price swings.',
    },
  },
  {
    id: 'intel',
    name: 'Intel',
    symbol: 'INTC',
    tokenIndex: getTokenIndexFromEnv('INTEL') ?? 63,
    marketIndex: getMarketIndexFromEnv('INTEL') ?? 1004,
    maxLeverage: 100,
    category: MARKET_CATEGORIES.STOCKS,
    tradingSchedule: TRADING_SCHEDULES.US_STOCKS,
    coinGeckoId: null,
    chartType: 'intel',
    tradingViewSymbol: 'NASDAQ:INTC',
    iconPath: '/assets/images/Perps/stocks/intel.svg',
    displayName: 'INTC/USDC',
    description: 'Intel Corporation',
    assetInfo: {
      what: 'Intel is a legacy semiconductor giant pivoting to a foundry model. Manufactures CPUs for PCs and servers and is investing heavily in US chip fabrication.',
      priceDriver: 'Stock price from NASDAQ. Driven by foundry strategy execution, PC market recovery, and government CHIPS Act subsidies.',
      risks: 'Market hours only. Turnaround story with execution risk. Lost market share to AMD and ARM-based designs.',
    },
  },
  {
    id: 'microsoft',
    name: 'Microsoft',
    symbol: 'MSFT',
    tokenIndex: getTokenIndexFromEnv('MICROSOFT') ?? 64,
    marketIndex: getMarketIndexFromEnv('MICROSOFT') ?? 1005,
    maxLeverage: 100,
    category: MARKET_CATEGORIES.STOCKS,
    tradingSchedule: TRADING_SCHEDULES.US_STOCKS,
    coinGeckoId: null,
    chartType: 'microsoft',
    tradingViewSymbol: 'NASDAQ:MSFT',
    iconPath: '/assets/images/Perps/stocks/msft.svg',
    displayName: 'MSFT/USDC',
    description: 'Microsoft Corporation',
    assetInfo: {
      what: 'Microsoft is the world\'s largest software company. Azure cloud, Office 365, Windows, Xbox, and a major AI investor via OpenAI partnership.',
      priceDriver: 'Stock price from NASDAQ. Driven by Azure cloud growth, AI Copilot monetization, and enterprise software demand.',
      risks: 'Market hours only. Mega-cap with lower volatility but sensitive to cloud growth deceleration and AI ROI scrutiny.',
    },
  },
  {
    id: 'apple',
    name: 'Apple',
    symbol: 'AAPL',
    tokenIndex: getTokenIndexFromEnv('APPLE') ?? 65,
    marketIndex: getMarketIndexFromEnv('APPLE') ?? 1006,
    maxLeverage: 100,
    category: MARKET_CATEGORIES.STOCKS,
    tradingSchedule: TRADING_SCHEDULES.US_STOCKS,
    coinGeckoId: null,
    chartType: 'apple',
    tradingViewSymbol: 'NASDAQ:AAPL',
    iconPath: '/assets/images/Perps/stocks/aapl.svg',
    displayName: 'AAPL/USDC',
    description: 'Apple Inc.',
    assetInfo: {
      what: 'Apple is the world\'s most valuable company. Designs iPhone, Mac, iPad, Apple Watch, and a growing Services business (App Store, Apple Music, iCloud).',
      priceDriver: 'Stock price from NASDAQ. Driven by iPhone upgrade cycles, Services revenue growth, and China market demand.',
      risks: 'Market hours only. Regulatory risk (App Store antitrust), China supply chain exposure, and hardware cycle dependency.',
    },
  },
  {
    id: 'alphabet',
    name: 'Alphabet',
    symbol: 'GOOGL',
    tokenIndex: getTokenIndexFromEnv('ALPHABET') ?? 66,
    marketIndex: getMarketIndexFromEnv('ALPHABET') ?? 1007,
    maxLeverage: 100,
    category: MARKET_CATEGORIES.STOCKS,
    tradingSchedule: TRADING_SCHEDULES.US_STOCKS,
    coinGeckoId: null,
    chartType: 'alphabet',
    tradingViewSymbol: 'NASDAQ:GOOGL',
    iconPath: '/assets/images/Perps/stocks/googl.svg',
    displayName: 'GOOGL/USDC',
    description: 'Alphabet Inc.',
    assetInfo: {
      what: 'Alphabet is Google\'s parent company. Dominates search advertising, YouTube, and Google Cloud. Major AI player with Gemini models and DeepMind.',
      priceDriver: 'Stock price from NASDAQ. Driven by ad revenue growth, Google Cloud performance, and AI product adoption.',
      risks: 'Market hours only. Antitrust litigation risk. AI competition from OpenAI/Microsoft could erode search monopoly.',
    },
  },
  {
    id: 'amazon',
    name: 'Amazon',
    symbol: 'AMZN',
    tokenIndex: getTokenIndexFromEnv('AMAZON') ?? 67,
    marketIndex: getMarketIndexFromEnv('AMAZON') ?? 1008,
    maxLeverage: 100,
    category: MARKET_CATEGORIES.STOCKS,
    tradingSchedule: TRADING_SCHEDULES.US_STOCKS,
    coinGeckoId: null,
    chartType: 'amazon',
    tradingViewSymbol: 'NASDAQ:AMZN',
    iconPath: '/assets/images/Perps/stocks/amzn.svg',
    displayName: 'AMZN/USDC',
    description: 'Amazon.com Inc.',
    assetInfo: {
      what: 'Amazon is the world\'s largest e-commerce and cloud computing company. AWS dominates cloud infrastructure; also operates advertising, streaming, and logistics businesses.',
      priceDriver: 'Stock price from NASDAQ. Driven by AWS growth, retail margins, and advertising revenue expansion.',
      risks: 'Market hours only. Sensitive to consumer spending and cloud capex cycles. Regulatory scrutiny on market dominance.',
    },
  },
  {
    id: 'meta-stock',
    name: 'Meta Platforms',
    symbol: 'META',
    tokenIndex: getTokenIndexFromEnv('META_STOCK') ?? 68,
    marketIndex: getMarketIndexFromEnv('META_STOCK') ?? 1009,
    maxLeverage: 100,
    category: MARKET_CATEGORIES.STOCKS,
    tradingSchedule: TRADING_SCHEDULES.US_STOCKS,
    coinGeckoId: null,
    chartType: 'meta-stock',
    tradingViewSymbol: 'NASDAQ:META',
    iconPath: '/assets/images/Perps/stocks/meta.svg',
    displayName: 'META/USDC',
    description: 'Meta Platforms Inc.',
    assetInfo: {
      what: 'Meta operates Facebook, Instagram, WhatsApp, and Threads — reaching ~4 billion users. Also investing heavily in AR/VR (Reality Labs) and AI infrastructure.',
      priceDriver: 'Stock price from NASDAQ. Driven by ad revenue, Reels monetization, and AI-driven engagement improvements.',
      risks: 'Market hours only. Reality Labs losses weigh on earnings. Regulatory risk in EU and potential TikTok competition shifts.',
    },
  },
  {
    id: 'broadcom',
    name: 'Broadcom',
    symbol: 'AVGO',
    tokenIndex: getTokenIndexFromEnv('BROADCOM') ?? 69,
    marketIndex: getMarketIndexFromEnv('BROADCOM') ?? 1010,
    maxLeverage: 100,
    category: MARKET_CATEGORIES.STOCKS,
    tradingSchedule: TRADING_SCHEDULES.US_STOCKS,
    coinGeckoId: null,
    chartType: 'broadcom',
    tradingViewSymbol: 'NASDAQ:AVGO',
    iconPath: '/assets/images/Perps/stocks/avgo.svg',
    displayName: 'AVGO/USDC',
    description: 'Broadcom Inc.',
    assetInfo: {
      what: 'Broadcom is a diversified semiconductor and infrastructure software conglomerate. Key supplier of networking chips for data centers and custom AI accelerators (TPUs for Google).',
      priceDriver: 'Stock price from NASDAQ. Driven by AI networking demand, VMware integration, and hyperscaler custom chip orders.',
      risks: 'Market hours only. Revenue concentration in a few large customers. Integration risk from major acquisitions.',
    },
  },
  {
    id: 'tesla',
    name: 'Tesla',
    symbol: 'TSLA',
    tokenIndex: getTokenIndexFromEnv('TESLA') ?? 70,
    marketIndex: getMarketIndexFromEnv('TESLA') ?? 1011,
    maxLeverage: 100,
    category: MARKET_CATEGORIES.STOCKS,
    tradingSchedule: TRADING_SCHEDULES.US_STOCKS,
    coinGeckoId: null,
    chartType: 'tesla',
    tradingViewSymbol: 'NASDAQ:TSLA',
    iconPath: '/assets/images/Perps/stocks/tsla.svg',
    displayName: 'TSLA/USDC',
    description: 'Tesla Inc.',
    assetInfo: {
      what: 'Tesla is the world\'s leading EV manufacturer and energy storage company. Also developing autonomous driving (FSD) and humanoid robotics (Optimus).',
      priceDriver: 'Stock price from NASDAQ. Driven by vehicle delivery numbers, margin trends, FSD progress, and Elon Musk sentiment.',
      risks: 'Market hours only. Extremely high volatility. Valuation includes significant optionality on autonomy and robotics.',
    },
  },
  {
    id: 'berkshire',
    name: 'Berkshire Hathaway',
    symbol: 'BRK.B',
    tokenIndex: getTokenIndexFromEnv('BERKSHIRE') ?? 71,
    marketIndex: getMarketIndexFromEnv('BERKSHIRE') ?? 1012,
    maxLeverage: 100,
    category: MARKET_CATEGORIES.STOCKS,
    tradingSchedule: TRADING_SCHEDULES.US_STOCKS,
    coinGeckoId: null,
    chartType: 'berkshire',
    tradingViewSymbol: 'NYSE:BRK.B',
    iconPath: '/assets/images/Perps/stocks/brkb.svg',
    displayName: 'BRK.B/USDC',
    description: 'Berkshire Hathaway Inc.',
    assetInfo: {
      what: 'Berkshire Hathaway is Warren Buffett\'s conglomerate. Owns GEICO, BNSF Railway, Dairy Queen, and massive equity stakes (Apple, Coca-Cola, Amex). ~$300B cash hoard.',
      priceDriver: 'Stock price from NYSE. Driven by insurance underwriting results, investment portfolio performance, and capital allocation decisions.',
      risks: 'Market hours only. Lower volatility than most stocks. Key-person risk around Warren Buffett\'s succession.',
    },
  },
  {
    id: 'coinbase-stock',
    name: 'Coinbase',
    symbol: 'COIN',
    tokenIndex: getTokenIndexFromEnv('COINBASE_STOCK') ?? 72,
    marketIndex: getMarketIndexFromEnv('COINBASE_STOCK') ?? 1013,
    maxLeverage: 100,
    category: MARKET_CATEGORIES.STOCKS,
    tradingSchedule: TRADING_SCHEDULES.US_STOCKS,
    coinGeckoId: null,
    chartType: 'coinbase-stock',
    tradingViewSymbol: 'NASDAQ:COIN',
    iconPath: '/assets/images/Perps/stocks/coin.svg',
    displayName: 'COIN/USDC',
    description: 'Coinbase Global Inc.',
    assetInfo: {
      what: 'Coinbase is the largest US-listed crypto exchange. Revenue from trading fees, staking, USDC custody, and Base L2 network. A proxy for crypto market activity.',
      priceDriver: 'Stock price from NASDAQ. Highly correlated with BTC/ETH prices and crypto trading volumes.',
      risks: 'Market hours only. Revenue is cyclical — tied to crypto bull/bear markets. SEC regulatory overhang.',
    },
  },
  {
    id: 'jpmorgan',
    name: 'JPMorgan Chase',
    symbol: 'JPM',
    tokenIndex: getTokenIndexFromEnv('JPMORGAN') ?? 73,
    marketIndex: getMarketIndexFromEnv('JPMORGAN') ?? 1014,
    maxLeverage: 100,
    category: MARKET_CATEGORIES.STOCKS,
    tradingSchedule: TRADING_SCHEDULES.US_STOCKS,
    coinGeckoId: null,
    chartType: 'jpmorgan',
    tradingViewSymbol: 'NYSE:JPM',
    iconPath: '/assets/images/Perps/stocks/jpm.svg',
    displayName: 'JPM/USDC',
    description: 'JPMorgan Chase & Co.',
    assetInfo: {
      what: 'JPMorgan Chase is the largest US bank by assets. Operates investment banking, commercial banking, asset management, and consumer banking divisions.',
      priceDriver: 'Stock price from NYSE. Driven by interest rate environment, loan growth, trading revenue, and credit quality.',
      risks: 'Market hours only. Sensitive to yield curve, credit cycles, and banking regulation changes.',
    },
  },
  {
    id: 'palantir',
    name: 'Palantir',
    symbol: 'PLTR',
    tokenIndex: getTokenIndexFromEnv('PALANTIR') ?? 74,
    marketIndex: getMarketIndexFromEnv('PALANTIR') ?? 1015,
    maxLeverage: 100,
    category: MARKET_CATEGORIES.STOCKS,
    tradingSchedule: TRADING_SCHEDULES.US_STOCKS,
    coinGeckoId: null,
    chartType: 'palantir',
    tradingViewSymbol: 'NASDAQ:PLTR',
    iconPath: '/assets/images/Perps/stocks/pltr.svg',
    displayName: 'PLTR/USDC',
    description: 'Palantir Technologies Inc.',
    assetInfo: {
      what: 'Palantir builds AI/data analytics platforms for government and commercial clients. Known for Gotham (defense/intel) and Foundry (enterprise) platforms, plus AIP for AI deployment.',
      priceDriver: 'Stock price from NASDAQ. Driven by government contract wins, commercial customer growth, and AI platform adoption.',
      risks: 'Market hours only. High valuation relative to revenue. Government contract concentration and long sales cycles.',
    },
  },
  {
    id: 'dwave',
    name: 'D-Wave Quantum',
    symbol: 'QBTS',
    tokenIndex: getTokenIndexFromEnv('DWAVE') ?? 75,
    marketIndex: getMarketIndexFromEnv('DWAVE') ?? 1016,
    maxLeverage: 100,
    category: MARKET_CATEGORIES.STOCKS,
    tradingSchedule: TRADING_SCHEDULES.US_STOCKS,
    coinGeckoId: null,
    chartType: 'dwave',
    tradingViewSymbol: 'NYSE:QBTS',
    iconPath: '/assets/images/Perps/stocks/qbts.svg',
    displayName: 'QBTS/USDC',
    description: 'D-Wave Quantum Inc.',
    assetInfo: {
      what: 'D-Wave is a quantum computing company offering the first commercial quantum annealing systems. Provides quantum cloud services for optimization problems.',
      priceDriver: 'Stock price from NYSE. Driven by quantum computing hype cycles, partnership announcements, and government quantum initiatives.',
      risks: 'Market hours only. Pre-revenue at scale — highly speculative. Quantum computing commercialization timeline is uncertain.',
    },
  },
  {
    id: 'quantum',
    name: 'Quantum Computing',
    symbol: 'QUBT',
    tokenIndex: getTokenIndexFromEnv('QUANTUM') ?? 76,
    marketIndex: getMarketIndexFromEnv('QUANTUM') ?? 1017,
    maxLeverage: 100,
    category: MARKET_CATEGORIES.STOCKS,
    tradingSchedule: TRADING_SCHEDULES.US_STOCKS,
    coinGeckoId: null,
    chartType: 'quantum',
    tradingViewSymbol: 'NASDAQ:QUBT',
    iconPath: '/assets/images/Perps/stocks/qubt.svg',
    displayName: 'QUBT/USDC',
    description: 'Quantum Computing Inc.',
    assetInfo: {
      what: 'Quantum Computing Inc. develops accessible quantum software and photonic chip technology. Focuses on making quantum optimization available to enterprises.',
      priceDriver: 'Stock price from NASDAQ. Driven by quantum computing sector sentiment, contract wins, and technology milestones.',
      risks: 'Market hours only. Micro-cap with very high volatility. Early-stage quantum company with limited revenue.',
    },
  },
  {
    id: 'gme',
    name: 'GameStop',
    symbol: 'GME',
    tokenIndex: getTokenIndexFromEnv('GME') ?? (isTestnet() ? 88 : 84),
    marketIndex: getMarketIndexFromEnv('GME') ?? (isTestnet() ? 83 : 1025),
    maxLeverage: 100,
    category: MARKET_CATEGORIES.STOCKS,
    tradingSchedule: TRADING_SCHEDULES.US_STOCKS,
    coinGeckoId: null,
    chartType: 'gme',
    tradingViewSymbol: 'NYSE:GME',
    iconPath: '/assets/images/Perps/stocks/gme.svg',
    displayName: 'GME/USDC',
    description: 'GameStop Corp.',
    assetInfo: {
      what: 'GameStop (GME) is a US video-game and consumer-electronics retailer that became the original "meme stock" during the 2021 retail-trading frenzy. It now holds a large cash and treasury position and is led by chairman Ryan Cohen.',
      priceDriver: 'Stock price from NYSE. Driven largely by retail and social-media sentiment, treasury and capital-allocation moves, and short-interest dynamics rather than traditional retail fundamentals.',
      risks: 'Market hours only. Extremely high volatility driven by sentiment and short squeezes. Can move sharply on social-media activity independent of business performance.',
    },
  },

  // ── Commodity Markets ─────────────────────────────────────────────
  // Group: equities_commodities (GroupIndex 4)
  // Trading hours: Sun 6 PM – Fri 5 PM ET (1-hr daily break 5–6 PM ET)
  // On-chain: TokenIndex 77–83, MarketIndex 1018–1024

  // Precious Metals
  {
    id: 'xau-usd',
    name: 'Gold',
    symbol: 'XAU',
    tokenIndex: getTokenIndexFromEnv('XAU_USD') ?? 77,
    marketIndex: getMarketIndexFromEnv('XAU_USD') ?? 1018,
    maxLeverage: 100,
    category: MARKET_CATEGORIES.COMMODITIES,
    tradingSchedule: TRADING_SCHEDULES.US_COMMODITIES,
    coinGeckoId: null,
    chartType: 'xau-usd',
    tradingViewSymbol: 'TVC:GOLD',
    iconPath: '/assets/images/Perps/commodities/xau.svg',
    displayName: 'XAU/USD',
    description: 'Gold Spot',
    assetInfo: {
      what: 'Gold (XAU/USD) is the world\'s primary safe-haven asset and store of value. Traded globally on COMEX and London Bullion Market. Central banks hold ~36,000 tonnes.',
      priceDriver: 'Spot gold price. Driven by real interest rates, USD strength, central bank buying, and geopolitical risk.',
      risks: 'Near-24hr trading (Sun–Fri with 1hr daily break). Lower volatility than equities but sensitive to Fed policy pivots and dollar moves.',
    },
  },
  {
    id: 'xag-usd',
    name: 'Silver',
    symbol: 'XAG',
    tokenIndex: getTokenIndexFromEnv('XAG_USD') ?? 78,
    marketIndex: getMarketIndexFromEnv('XAG_USD') ?? 1019,
    maxLeverage: 100,
    category: MARKET_CATEGORIES.COMMODITIES,
    tradingSchedule: TRADING_SCHEDULES.US_COMMODITIES,
    coinGeckoId: null,
    chartType: 'xag-usd',
    tradingViewSymbol: 'TVC:SILVER',
    iconPath: '/assets/images/Perps/commodities/xag.svg',
    displayName: 'XAG/USD',
    description: 'Silver Spot',
    assetInfo: {
      what: 'Silver (XAG/USD) is both a precious metal and an industrial commodity. Used in electronics, solar panels, and jewelry. More volatile than gold.',
      priceDriver: 'Spot silver price. Driven by gold correlation, industrial demand (especially solar), and investment flows.',
      risks: 'Near-24hr trading. Higher volatility than gold due to smaller market and dual industrial/precious nature.',
    },
  },
  {
    id: 'xpt-usd',
    name: 'Platinum',
    symbol: 'XPT',
    tokenIndex: getTokenIndexFromEnv('XPT_USD') ?? 79,
    marketIndex: getMarketIndexFromEnv('XPT_USD') ?? 1020,
    maxLeverage: 100,
    category: MARKET_CATEGORIES.COMMODITIES,
    tradingSchedule: TRADING_SCHEDULES.US_COMMODITIES,
    coinGeckoId: null,
    chartType: 'xpt-usd',
    tradingViewSymbol: 'TVC:PLATINUM',
    iconPath: '/assets/images/Perps/commodities/xpt.svg',
    displayName: 'XPT/USD',
    description: 'Platinum Spot',
    assetInfo: {
      what: 'Platinum (XPT/USD) is a rare precious metal used in auto catalytic converters, jewelry, and hydrogen fuel cells. Supply concentrated in South Africa.',
      priceDriver: 'Spot platinum price. Driven by auto industry demand, hydrogen economy narrative, and South African mining supply.',
      risks: 'Near-24hr trading. Less liquid than gold/silver. Supply disruptions from South Africa can cause sharp moves.',
    },
  },
  {
    id: 'xpd-usd',
    name: 'Palladium',
    symbol: 'XPD',
    tokenIndex: getTokenIndexFromEnv('XPD_USD') ?? 80,
    marketIndex: getMarketIndexFromEnv('XPD_USD') ?? 1021,
    maxLeverage: 100,
    category: MARKET_CATEGORIES.COMMODITIES,
    tradingSchedule: TRADING_SCHEDULES.US_COMMODITIES,
    coinGeckoId: null,
    chartType: 'xpd-usd',
    tradingViewSymbol: 'TVC:PALLADIUM',
    iconPath: '/assets/images/Perps/commodities/xpd.svg',
    displayName: 'XPD/USD',
    description: 'Palladium Spot',
    assetInfo: {
      what: 'Palladium (XPD/USD) is a platinum-group metal primarily used in gasoline catalytic converters. Supply dominated by Russia and South Africa.',
      priceDriver: 'Spot palladium price. Driven by auto production volumes, emissions regulation, and EV adoption (reduces demand).',
      risks: 'Near-24hr trading. High volatility. EV transition is a structural headwind for long-term demand.',
    },
  },

  // Energy
  {
    id: 'wti-usd',
    name: 'WTI Crude Oil',
    symbol: 'WTI',
    tokenIndex: getTokenIndexFromEnv('WTI_USD') ?? 81,
    marketIndex: getMarketIndexFromEnv('WTI_USD') ?? 1022,
    maxLeverage: 100,
    category: MARKET_CATEGORIES.COMMODITIES,
    tradingSchedule: TRADING_SCHEDULES.US_COMMODITIES,
    coinGeckoId: null,
    chartType: 'wti-usd',
    tradingViewSymbol: 'TVC:USOIL',
    iconPath: '/assets/images/Perps/commodities/oil.svg',
    displayName: 'WTI/USD',
    description: 'WTI Crude Oil',
    assetInfo: {
      what: 'West Texas Intermediate (WTI) is the US benchmark for crude oil, delivered at Cushing, Oklahoma. The most actively traded commodity futures contract globally.',
      priceDriver: 'WTI front-month futures price. Driven by OPEC+ production decisions, US shale output, EIA inventory reports, and global demand.',
      risks: 'Near-24hr trading. Highly volatile — geopolitical events (Middle East, Russia) can cause sudden spikes or crashes.',
    },
  },
  {
    id: 'xbr-usd',
    name: 'Brent Crude Oil',
    symbol: 'XBR',
    tokenIndex: getTokenIndexFromEnv('XBR_USD') ?? 82,
    marketIndex: getMarketIndexFromEnv('XBR_USD') ?? 1023,
    maxLeverage: 100,
    category: MARKET_CATEGORIES.COMMODITIES,
    tradingSchedule: TRADING_SCHEDULES.US_COMMODITIES,
    coinGeckoId: null,
    chartType: 'xbr-usd',
    tradingViewSymbol: 'TVC:UKOIL',
    iconPath: '/assets/images/Perps/commodities/oil.svg',
    displayName: 'XBR/USD',
    description: 'Brent Crude Oil',
    assetInfo: {
      what: 'Brent Crude is the international benchmark for oil, priced off North Sea production. Used to price roughly two-thirds of the world\'s traded crude oil.',
      priceDriver: 'Brent front-month futures price. Driven by OPEC+ policy, global shipping/demand, and geopolitical supply risk.',
      risks: 'Near-24hr trading. Typically trades at a premium to WTI. Sensitive to Middle East geopolitics and European demand.',
    },
  },
  {
    id: 'ng-usd',
    name: 'Natural Gas',
    symbol: 'NG',
    tokenIndex: getTokenIndexFromEnv('NG_USD') ?? 83,
    marketIndex: getMarketIndexFromEnv('NG_USD') ?? 1024,
    maxLeverage: 100,
    category: MARKET_CATEGORIES.COMMODITIES,
    tradingSchedule: TRADING_SCHEDULES.US_COMMODITIES,
    coinGeckoId: null,
    chartType: 'ng-usd',
    tradingViewSymbol: 'CAPITALCOM:NATURALGAS',
    iconPath: '/assets/images/Perps/commodities/gas.svg',
    displayName: 'NG/USD',
    description: 'Natural Gas',
    assetInfo: {
      what: 'Henry Hub Natural Gas is the US benchmark for natural gas, delivered at the Henry Hub in Louisiana. Used for power generation, heating, and industrial processes.',
      priceDriver: 'NG front-month futures price. Driven by weather forecasts, EIA storage reports, LNG export demand, and production levels.',
      risks: 'Near-24hr trading. Extremely volatile — weather events and seasonal demand swings can move prices 10%+ in a day.',
    },
  },
  
  // Real Estate Markets
  // Dubai commented out for now
  /*
  {
    id: 'dubai',
    name: 'Dubai',
    symbol: 'DUBAI',
    tokenIndex: getTokenIndexFromEnv('DUBAI') ?? 6,
    marketIndex: getMarketIndexFromEnv('DUBAI') ?? (isTestnet() ? 3 : 2),
    maxLeverage: 100,
    category: MARKET_CATEGORIES.REAL_ESTATE,
    coinGeckoId: null,
    chartType: 'dubai',
    iconPath: '/assets/images/Perps/UAE.png',
    displayName: 'DUBAI/USDC',
    description: 'Dubai Real Estate',
    marketCode: 'AE-DXB',
  },
  */
  {
    id: 'los-angeles',
    name: 'Los Angeles',
    symbol: 'LOSANGELES',
    tokenIndex: getTokenIndexFromEnv('LOS_ANGELES') ?? 7,
    marketIndex: getMarketIndexFromEnv('LOS_ANGELES') ?? (isTestnet() ? 4 : 3),
    maxLeverage: 100,
    category: MARKET_CATEGORIES.REAL_ESTATE,
    coinGeckoId: null,
    chartType: 'los-angeles',
    iconPath: '/assets/images/Perps/USA.svg',
    displayName: 'LOSANGELES/USDC',
    description: 'Los Angeles Real Estate',
    marketCode: 'CA-LA',
    assetInfo: {
      what: 'Tracks the per-sqft price of residential real estate in Los Angeles, CA. Covers single-family homes and condos across LA County.',
      priceDriver: 'Rolling $/sqft index computed from verified property transaction data. Price updates once daily.',
      risks: 'Prices update once daily, giving you time to react to market moves. Driven by interest rates, tech employment, and California\'s evolving regulatory landscape — a well-followed market with clear macro catalysts.',
    },
  },
  {
    id: 'new-york',
    name: 'New York',
    symbol: 'NEWYORK',
    tokenIndex: getTokenIndexFromEnv('NEW_YORK') ?? 8,
    marketIndex: getMarketIndexFromEnv('NEW_YORK') ?? (isTestnet() ? 5 : 4),
    maxLeverage: 100,
    category: MARKET_CATEGORIES.REAL_ESTATE,
    coinGeckoId: null,
    chartType: 'new-york',
    iconPath: '/assets/images/Perps/USA.svg',
    displayName: 'NEWYORK/USDC',
    description: 'New York Real Estate',
    marketCode: 'NY-NYC',
    assetInfo: {
      what: 'Tracks the per-sqft price of residential real estate in New York City (Manhattan, Brooklyn, Queens, Bronx, Staten Island).',
      priceDriver: 'Rolling $/sqft index from verified NYC property transaction data. Price updates once daily.',
      risks: 'Prices update once daily. NYC is among the most resilient real estate markets in the world — driven by Wall Street cycles, interest rates, and steady institutional demand.',
    },
  },
  {
    id: 'chicago',
    name: 'Chicago',
    symbol: 'CHICAGO',
    tokenIndex: getTokenIndexFromEnv('CHICAGO') ?? 9,
    marketIndex: getMarketIndexFromEnv('CHICAGO') ?? (isTestnet() ? 6 : 5),
    maxLeverage: 100,
    category: MARKET_CATEGORIES.REAL_ESTATE,
    coinGeckoId: null,
    chartType: 'chicago',
    iconPath: '/assets/images/Perps/USA.svg',
    displayName: 'CHICAGO/USDC',
    description: 'Chicago Real Estate',
    marketCode: 'IL-CHI',
    assetInfo: {
      what: 'Tracks the per-sqft price of residential real estate in Chicago, IL. One of the most affordable major US metro markets.',
      priceDriver: 'Transaction-based $/sqft index for Cook County and surrounding areas. Price updates once daily.',
      risks: 'Prices update once daily. Chicago offers attractive entry points relative to coastal cities, with steady demand driven by a diversified local economy and population flows.',
    },
  },
  {
    id: 'washington',
    name: 'Washington',
    symbol: 'WASHINGTON',
    tokenIndex: getTokenIndexFromEnv('WASHINGTON') ?? 10,
    marketIndex: getMarketIndexFromEnv('WASHINGTON') ?? (isTestnet() ? 7 : 6),
    maxLeverage: 100,
    category: MARKET_CATEGORIES.REAL_ESTATE,
    coinGeckoId: null,
    chartType: 'washington',
    iconPath: '/assets/images/Perps/USA.svg',
    displayName: 'WASHINGTON/USDC',
    description: 'Washington Real Estate',
    marketCode: 'DC-WAS',
    assetInfo: {
      what: 'Tracks the per-sqft price of residential real estate in Washington D.C. and the broader DMV area.',
      priceDriver: 'Transaction-based $/sqft index for the DC metro. Price updates once daily.',
      risks: 'Prices update once daily. DC is one of the most stable US real estate markets, anchored by federal government employment and strong institutional demand. Less volatile than tech-driven metros.',
    },
  },
  {
    id: 'pittsburgh',
    name: 'Pittsburgh',
    symbol: 'PITTSBURGH',
    tokenIndex: getTokenIndexFromEnv('PITTSBURGH') ?? 11,
    marketIndex: getMarketIndexFromEnv('PITTSBURGH') ?? (isTestnet() ? 8 : 7),
    maxLeverage: 100,
    category: MARKET_CATEGORIES.REAL_ESTATE,
    coinGeckoId: null,
    chartType: 'pittsburgh',
    iconPath: '/assets/images/Perps/USA.svg',
    displayName: 'PITTSBURGH/USDC',
    description: 'Pittsburgh Real Estate',
    marketCode: 'PA-PIT',
    assetInfo: {
      what: 'Tracks the per-sqft price of residential real estate in Pittsburgh, PA. One of the most affordable major metros in the US with a growing tech and healthcare sector.',
      priceDriver: 'Transaction-based $/sqft index for Allegheny County. Price updates once daily.',
      risks: 'Prices update once daily. Pittsburgh offers one of the best value propositions among US metros, with a growing tech and healthcare sector providing a solid demand floor.',
    },
  },
  {
    id: 'miami-beach',
    name: 'Miami Beach',
    symbol: 'MIAMIBEACH',
    tokenIndex: getTokenIndexFromEnv('MIAMI_BEACH') ?? 12,
    marketIndex: getMarketIndexFromEnv('MIAMI_BEACH') ?? (isTestnet() ? 9 : 8),
    maxLeverage: 100,
    category: MARKET_CATEGORIES.REAL_ESTATE,
    coinGeckoId: null,
    chartType: 'miami-beach',
    iconPath: '/assets/images/Perps/USA.svg',
    displayName: 'MIAMIBEACH/USDC',
    description: 'Miami Beach Real Estate',
    marketCode: 'FL-MB',
    assetInfo: {
      what: 'Tracks the per-sqft price of residential real estate in Miami Beach, FL. A premium coastal market with significant demand from international buyers, remote workers, and crypto wealth.',
      priceDriver: '$/sqft index for Miami Beach proper (barrier island). Price updates once daily.',
      risks: 'Prices update once daily. Miami Beach is a globally sought-after premium market with strong international buyer demand, remote worker inflows, and growing crypto-native wealth fueling long-term appreciation.',
    },
  },
  {
    id: 'boston',
    name: 'Boston',
    symbol: 'BOSTON',
    tokenIndex: getTokenIndexFromEnv('BOSTON') ?? 13,
    marketIndex: getMarketIndexFromEnv('BOSTON') ?? (isTestnet() ? 10 : 9),
    maxLeverage: 100,
    category: MARKET_CATEGORIES.REAL_ESTATE,
    coinGeckoId: null,
    chartType: 'boston',
    iconPath: '/assets/images/Perps/USA.svg',
    displayName: 'BOSTON/USDC',
    description: 'Boston Real Estate',
    marketCode: 'MA-BOS',
    assetInfo: {
      what: 'Tracks the per-sqft price of residential real estate in Boston, MA. A high-demand market driven by university presence (Harvard, MIT) and biotech/life sciences employment.',
      priceDriver: '$/sqft index for Suffolk County and metro Boston. Price updates once daily.',
      risks: 'Prices update once daily. Boston benefits from world-class universities (Harvard, MIT) and a booming biotech sector. Limited housing supply helps support prices even during broader downturns.',
    },
  },
  {
    id: 'brooklyn',
    name: 'Brooklyn',
    symbol: 'BROOKLYN',
    tokenIndex: getTokenIndexFromEnv('BROOKLYN') ?? 14,
    marketIndex: getMarketIndexFromEnv('BROOKLYN') ?? (isTestnet() ? 11 : 10),
    maxLeverage: 100,
    category: MARKET_CATEGORIES.REAL_ESTATE,
    coinGeckoId: null,
    chartType: 'brooklyn',
    iconPath: '/assets/images/Perps/USA.svg',
    displayName: 'BROOKLYN/USDC',
    description: 'Brooklyn Real Estate',
    marketCode: 'NY-BRK',
    assetInfo: {
      what: 'Tracks the per-sqft price of residential real estate in Brooklyn, NY — NYC\'s most populous borough and one of the fastest-appreciating markets in the US.',
      priceDriver: '$/sqft index for Kings County (Brooklyn). Price updates once daily.',
      risks: 'Prices update once daily. Brooklyn has been one of the strongest appreciating markets in the US, closely tracking Manhattan with growing cultural and economic momentum.',
    },
  },
  {
    id: 'austin',
    name: 'Austin',
    symbol: 'AUSTIN',
    tokenIndex: getTokenIndexFromEnv('AUSTIN') ?? 15,
    marketIndex: getMarketIndexFromEnv('AUSTIN') ?? (isTestnet() ? 12 : 11),
    maxLeverage: 100,
    category: MARKET_CATEGORIES.REAL_ESTATE,
    coinGeckoId: null,
    chartType: 'austin',
    iconPath: '/assets/images/Perps/USA.svg',
    displayName: 'AUSTIN/USDC',
    description: 'Austin Real Estate',
    marketCode: 'TX-AUS',
    assetInfo: {
      what: 'Tracks the per-sqft price of residential real estate in Austin, TX. The market surged during the post-COVID tech migration boom and has since seen notable correction.',
      priceDriver: '$/sqft index for Travis County. Price updates once daily.',
      risks: 'Prices update once daily. Austin has corrected from its 2020–2022 highs, potentially offering attractive re-entry points. Continued tech migration and population growth support long-term demand.',
    },
  },
  {
    id: 'denver',
    name: 'Denver',
    symbol: 'DENVER',
    tokenIndex: getTokenIndexFromEnv('DENVER') ?? 16,
    marketIndex: getMarketIndexFromEnv('DENVER') ?? (isTestnet() ? 13 : 12),
    maxLeverage: 100,
    category: MARKET_CATEGORIES.REAL_ESTATE,
    coinGeckoId: null,
    chartType: 'denver',
    iconPath: '/assets/images/Perps/USA.svg',
    displayName: 'DENVER/USDC',
    description: 'Denver Real Estate',
    marketCode: 'CO-DEN',
    assetInfo: {
      what: 'Tracks the per-sqft price of residential real estate in Denver, CO. A strong outdoor-lifestyle market with high demand from remote workers and Rocky Mountain amenity seekers.',
      priceDriver: '$/sqft index for Denver County and the Denver-Aurora metro. Price updates once daily.',
      risks: 'Prices update once daily. Denver\'s outdoor-lifestyle appeal and steady population inflows from remote workers make it a compelling long-term market. Recent softening may present opportunities at better entry points.',
    },
  },

  // PSA-10 — live since 2026-08-10.
  //   Mainnet: TokenIndex(85), MarketIndex(1026)
  //   Testnet: TokenIndex(90), MarketIndex(84)
  // The tradeable price comes from the oracle (fed by update_system's
  // PSA10Scraper daily + PSA10HourlyScraper hourly). Chart history and the
  // 24h badge come from Perps-Server /psa10/* (MongoDB psa10candles,
  // daily+hourly candles in one collection) — the same split POKE uses.
  {
    id: 'psa-10',
    name: 'PSA 10',
    symbol: 'PSA-10',
    tokenIndex: getTokenIndexFromEnv('PSA_10') ?? (isTestnet() ? 90 : 85),
    marketIndex: getMarketIndexFromEnv('PSA_10') ?? (isTestnet() ? 84 : 1026),
    // The market was opened at 1-10x on both networks. Unlike POKE, this is the
    // contract's actual cap, so the leverage bar must not offer more.
    maxLeverage: 10,
    category: MARKET_CATEGORIES.RWA,
    coinGeckoId: null,
    chartType: 'psa10',
    tradingViewSymbol: null,
    iconPath: '/assets/images/Perps/psa10-card.png',
    cardShape: true, // render the asset image as a portrait Pokémon card
    displayName: 'PSA-10/USDC',
    description: 'PSA 10 Pokémon Index',
    assetInfo: {
      what: 'The PSA-10 Index (PDI-PSA10) tracks the 10 most liquid PSA 10 (Gem Mint) Pokémon cards, composite-weighted by liquidity, dollar volume, and inverse volatility. Inception 2025-07-01 = 1000.',
      priceDriver: 'Daily index value from PriceDepth, recomputed from authenticated PSA 10 secondary-market sales. Price updates once daily.',
      risks: 'Prices update once daily, so the market can gap between updates. The graded-collectibles market can be illiquid and volatile around individual card sales. Leverage is capped at 10x.',
    },
  },
];

/**
 * Get market by ID
 * @param {string} id - Market ID
 * @returns {Object|null} Market configuration or null
 */
export const getMarketById = (id) => {
  return MARKETS.find(market => market.id === id) || null;
};

/**
 * Get market by token index
 * @param {number|string} tokenIndex - TokenIndex number or string like "TokenIndex(3)"
 * @returns {Object|null} Market configuration or null
 */
export const getMarketByTokenIndex = (tokenIndex) => {
  let parsedIndex;
  if (typeof tokenIndex === 'string') {
    const match = tokenIndex.match(/TokenIndex\((\d+)\)/);
    if (match) {
      parsedIndex = parseInt(match[1], 10);
    } else {
      parsedIndex = parseInt(tokenIndex, 10);
    }
  } else {
    parsedIndex = tokenIndex;
  }
  
  return MARKETS.find(market => market.tokenIndex === parsedIndex) || null;
};

/**
 * Get market by market index
 * @param {number|string} marketIndex - MarketIndex number or string like "MarketIndex(0)"
 * @returns {Object|null} Market configuration or null
 */
export const getMarketByMarketIndex = (marketIndex) => {
  let parsedIndex;
  if (typeof marketIndex === 'string') {
    const match = marketIndex.match(/MarketIndex\((\d+)\)/);
    if (match) {
      parsedIndex = parseInt(match[1], 10);
    } else {
      parsedIndex = parseInt(marketIndex, 10);
    }
  } else {
    parsedIndex = marketIndex;
  }
  
  return MARKETS.find(market => market.marketIndex === parsedIndex) || null;
};

/**
 * Get markets by category
 * @param {string} category - Market category
 * @returns {Array} Array of market configurations
 */
export const getMarketsByCategory = (category) => {
  return MARKETS.filter(market => market.category === category);
};

/**
 * Get markets by max leverage
 * @param {number} leverage - Maximum leverage (e.g., 100 for 100x)
 * @returns {Array} Array of market configurations
 */
export const getMarketsByLeverage = (leverage) => {
  return MARKETS.filter(market => market.maxLeverage === leverage);
};

/**
 * Get markets that have both MarketIndex and specific leverage
 * @param {number} leverage - Maximum leverage
 * @returns {Array} Array of market configurations
 */
export const getMarketsWithMarketIndexAndLeverage = (leverage) => {
  return MARKETS.filter(
    market => market.marketIndex !== null && market.marketIndex !== undefined && market.maxLeverage === leverage
  );
};

/**
 * Get CoinGecko ID from token index (for backward compatibility)
 * Maps to CoinGecko API IDs for price fetching
 * @param {number|string} tokenIndex - TokenIndex number or string
 * @returns {string|null} CoinGecko ID or null
 */
export const getCoinIdFromTokenIndex = (tokenIndex) => {
  const market = getMarketByTokenIndex(tokenIndex);
  if (market?.coinGeckoId) {
    return market.coinGeckoId;
  }
  
  // Fallback mapping for backward compatibility
  let parsedIndex;
  if (typeof tokenIndex === 'string') {
    const match = tokenIndex.match(/TokenIndex\((\d+)\)/);
    if (match) {
      parsedIndex = parseInt(match[1], 10);
    } else {
      parsedIndex = parseInt(tokenIndex, 10);
    }
  } else {
    parsedIndex = tokenIndex;
  }
  
  // Legacy mapping (for markets not yet in config)
  const legacyMap = {
    3: 'bitcoin',
    4: 'ethereum',
    5: 'cosmos',
  };
  
  return legacyMap[parsedIndex] || null;
};

/**
 * Get coin ID for TradingTopBar (backward compatibility)
 * Maps token index to coin identifier string
 * @param {number|string} tokenIndex - TokenIndex number or string
 * @returns {string} Coin ID string
 */
export const getCoinId = (tokenIndex) => {
  const market = getMarketByTokenIndex(tokenIndex);
  if (market) {
    // Return lowercase ID for coin identifier
    return market.id;
  }
  
  // Handle special cases that don't have markets using env vars
  const parsedIndex = parseTokenIndex(tokenIndex);
  
  // Check if index matches USDC or STNIBI from env
  const usdcIndex = getTokenIndexFromEnv('USDC');
  const stnibiIndex = getTokenIndexFromEnv('NIBI');
  
  if (usdcIndex !== null && parsedIndex === usdcIndex) return "usdc";
  if (stnibiIndex !== null && parsedIndex === stnibiIndex) return "stnibi";
  
  // Fallback for unknown indices
  console.log("Unknown token index:", parsedIndex);
  return "unknown";
};

/**
 * Format market index string
 * @param {number} index - Market index number
 * @returns {string} Formatted string like "MarketIndex(0)"
 */
export const formatMarketIndex = (index) => {
  return `MarketIndex(${index})`;
};

/**
 * Format token index string
 * @param {number} index - Token index number
 * @returns {string} Formatted string like "TokenIndex(3)"
 */
export const formatTokenIndex = (index) => {
  return `TokenIndex(${index})`;
};

/**
 * Get market display name from market index
 * @param {string|number} marketIndex - MarketIndex string or number
 * @returns {string} Display name like "BTC/USDC"
 */
export const getMarketName = (marketIndex) => {
  const market = getMarketByMarketIndex(marketIndex);
  return market?.displayName || "UNKNOWN/USDC";
};

/**
 * Check if market is a city/real estate market
 * @param {string} coinId - Coin ID
 * @returns {boolean} True if city market
 */
export const isCityMarket = (coinId) => {
  const market = getMarketById(coinId);
  return market?.category === MARKET_CATEGORIES.REAL_ESTATE;
};

/**
 * Get all crypto markets
 * @returns {Array} Array of crypto market configurations
 */
export const getCryptoMarkets = () => {
  return getMarketsByCategory(MARKET_CATEGORIES.CRYPTO);
};

/**
 * Get all real estate markets
 * @returns {Array} Array of real estate market configurations
 */
export const getRealEstateMarkets = () => {
  return getMarketsByCategory(MARKET_CATEGORIES.REAL_ESTATE);
};

/**
 * Get all RWA (Real World Asset) markets
 * @returns {Array} Array of RWA market configurations
 */
export const getRWAMarkets = () => {
  return getMarketsByCategory(MARKET_CATEGORIES.RWA);
};

/**
 * Get all Watch markets
 * @returns {Array} Array of Watch market configurations
 */
export const getWatchMarkets = () => {
  return MARKETS.filter(market => market.category === MARKET_CATEGORIES.WATCHES || market.watchBrandKey);
};

/**
 * Check if market is a watch market
 * @param {string} marketId - Market ID or chartType
 * @returns {boolean} True if watch market
 */
export const isWatchMarket = (marketId) => {
  const market = getMarketById(marketId);
  return market?.category === MARKET_CATEGORIES.WATCHES || !!market?.watchBrandKey;
};

/**
 * Get all markets with MarketIndex (tradable markets)
 * @returns {Array} Array of market configurations
 */
export const getTradableMarkets = () => {
  return MARKETS.filter(market => market.marketIndex !== null && market.marketIndex !== undefined);
};

/**
 * Get all stock markets
 * @returns {Array} Array of stock market configurations
 */
export const getStockMarkets = () => {
  return getMarketsByCategory(MARKET_CATEGORIES.STOCKS);
};

/**
 * Get all commodity markets
 * @returns {Array} Array of commodity market configurations
 */
export const getCommodityMarkets = () => {
  return getMarketsByCategory(MARKET_CATEGORIES.COMMODITIES);
};

/**
 * Get all PoC (proof-of-concept) markets
 * @returns {Array} Array of PoC market configurations
 */
export const getPocMarkets = () => {
  return MARKETS.filter(m => m.pocMode === true);
};

/**
 * Check if market is a stock market
 * @param {string} marketId - Market ID
 * @returns {boolean}
 */
export const isStockMarket = (marketId) => {
  const market = getMarketById(marketId);
  return market?.category === MARKET_CATEGORIES.STOCKS;
};

/**
 * Check if market is a commodity market
 * @param {string} marketId - Market ID
 * @returns {boolean}
 */
export const isCommodityMarket = (marketId) => {
  const market = getMarketById(marketId);
  return market?.category === MARKET_CATEGORIES.COMMODITIES;
};

/**
 * Check if a market has a trading schedule (time-restricted)
 * @param {string} marketId - Market ID
 * @returns {boolean}
 */
export const hasScheduledHours = (marketId) => {
  const market = getMarketById(marketId);
  return !!market?.tradingSchedule;
};


// Whole categories closed to new positions on the frontend, whatever the
// contract says. Real estate is wound down: existing positions can still be
// closed, but nobody can put new money in.
export const DISABLED_CATEGORIES = new Set([MARKET_CATEGORIES.REAL_ESTATE]);

/**
 * Check if a market is disabled by config (category-wide or `disabled: true`).
 * On-chain closures are layered on top by useDisabledMarkets.
 * @param {Object} market - Market configuration
 * @returns {boolean}
 */
export const isMarketDisabledByConfig = (market) => {
  return !!market && (market.disabled === true || DISABLED_CATEGORIES.has(market.category));
};
